<template>
  <div class="max-w-5xl mx-auto p-4 sm:p-6 relative">
    
    <!-- Header Controls: Stack on mobile, Top-right on desktop -->
    <div class="flex justify-end mb-8 sm:mb-0 sm:absolute sm:top-0 sm:right-0 sm:mt-6 sm:mr-6">
      <div class="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
        <span class="hidden sm:inline">Detected:</span>
        <img :src="flagUrl" class="flag-icon" alt="flag" />
        <div class="relative">
          <select
            v-model="selectedCountry"
            class="appearance-none bg-white border border-gray-300 rounded px-2 py-1 pr-6 text-sm focus:outline-none cursor-pointer"
          >
            <option value="US">United States</option>
            <option value="Canada">Canada</option>
          </select>
          <svg class="absolute right-2 top-2.5 w-3 h-3 text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Hero Input -->
    <div class="flex flex-col sm:flex-row justify-center items-center gap-3 text-2xl sm:text-4xl font-bold text-gray-900 mb-6 mt-4 sm:mt-24 text-center">
      <span>What if you invested</span>
      <div class="relative inline-block">
        <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg text-gray-500">$</span>
        <input
          type="text"
          inputmode="numeric"
          :value="displayValue"
          @input="handleInput"
          class="border border-gray-300 rounded shadow-sm bg-white py-2 pl-8 pr-2 w-40 text-center text-2xl focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>
      <span>at the start of {{ currentYear }}?</span>
    </div>

    <p class="text-center text-gray-600 text-lg mb-8 px-4">
      Here’s how it could have grown — by risk level.
    </p>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-12">
      <span class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></span>
    </div>

    <!-- Results Grid -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-12">
      <div
        v-for="item in computedResults"
        :key="item.risk"
        class="bg-white border rounded p-4 text-center shadow-sm transition hover:shadow-md mx-auto w-full max-w-xs sm:max-w-none"
        :class="tileBorderClass(item.risk)"
      >
        <h3 class="text-sm font-semibold mb-1 text-gray-700">{{ item.risk }}</h3>
        <p class="text-2xl font-bold text-gray-800">${{ formatNumber(item.finalValue) }}</p>
        <div class="mt-1 text-sm font-medium" :class="getReturnColor(item.returnPct)">
           {{ item.returnPct > 0 ? '+' : '' }}{{ item.returnPct }}%
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="text-center mb-10">
      <router-link
        to="/select-risk"
        class="bg-green-600 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:bg-green-700 transition inline-block w-full sm:w-auto"
      >
        Run Your Numbers →
      </router-link>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { detectUserCountry } from "@/utils/location";

export default {
  name: "HomePage",
  data() {
    return {
      rawValue: 10000, 
      selectedCountry: "US",
      multipliers: {}, 
      isLoading: false,
      currentYear: new Date().getFullYear()
    };
  },
  computed: {
    displayValue() {
      return this.rawValue.toLocaleString();
    },
    flagUrl() {
      return this.selectedCountry === "Canada"
        ? "https://flagcdn.com/ca.svg"
        : "https://flagcdn.com/us.svg";
    },
    computedResults() {
      const order = ["Risk-Free", "Conservative", "Balanced", "Aggressive"];
      return order.map((risk) => {
        const multiplier = this.multipliers[risk] || 1.0;
        const finalValue = this.rawValue * multiplier;
        const returnPct = ((multiplier - 1) * 100).toFixed(1);
        
        return {
          risk,
          finalValue,
          returnPct
        };
      });
    },
  },
  methods: {
    tileBorderClass(risk) {
      return {
        "border-gray-300": risk === "Risk-Free",
        "border-blue-400": risk === "Conservative",
        "border-yellow-500": risk === "Balanced",
        "border-red-500": risk === "Aggressive",
      };
    },
    getReturnColor(pct) {
      if (pct > 0) return "text-green-600";
      if (pct < 0) return "text-red-600";
      return "text-gray-500";
    },
    formatNumber(value) {
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    handleInput(event) {
      const val = event.target.value.replace(/[^0-9]/g, "");
      this.rawValue = val ? parseInt(val, 10) : 0;
    },
    async fetchMultipliers() {
      this.isLoading = true;
      try {
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:5000";
        const res = await axios.get(`${apiUrl}/homepage_data`, {
          params: { country: this.selectedCountry }
        });
        this.multipliers = res.data;
      } catch (err) {
        console.error("❌ Failed to fetch homepage data:", err);
        this.multipliers = {
            "Risk-Free": 1.0, "Conservative": 1.0, "Balanced": 1.0, "Aggressive": 1.0
        };
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    selectedCountry(val) {
      try { localStorage.setItem('selectedCountry', val); } catch (e) { /* ignore */ }
      this.fetchMultipliers(); 
    }
  },
  async mounted() {
    try {
        const saved = localStorage.getItem('selectedCountry');
        if (saved) {
            this.selectedCountry = saved;
        } else {
            const detected = await detectUserCountry();
            if (detected) this.selectedCountry = detected;
        }
    } catch(e) { console.log(e) }
    this.fetchMultipliers();
  },
};
</script>

<style scoped>
.flag-icon {
  width: 24px;          
  height: 16px;         
  object-fit: cover;    
  border: 1px solid #e5e7eb; 
  border-radius: 2px;
  display: block;
}
</style>